import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import "./App.css";
import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Player } from "@remotion/player";
import ProductVideo from "./remotion/ProductVideo";
import { getVideoDimensions } from "./utils/videoFormats";

function VideoPublico() {
  const { id: videoId } = useParams();
  const [video, setVideo] = useState(null);
  const [produtos, setProdutos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);

  const containerRef = useRef(null);
  const [playerSize, setPlayerSize] = useState({ width: 0, height: 0 });

  // Carregar dados do vídeo
  useEffect(() => {
    if (!videoId) {
      setErro("Nenhum vídeo especificado.");
      setLoading(false);
      return;
    }

    Promise.all([
      fetch(`https://videooferta.com/api/video.php?id=${videoId}`).then(r => r.json()),
      fetch(`https://videooferta.com/api/listarProdutos.php?id=${videoId}`).then(r => r.json())
    ])
      .then(([videoData, produtosData]) => {
        if (!videoData || videoData.erro) {
          setErro("Vídeo não encontrado.");
        } else {
          setVideo(videoData);
          setProdutos(Array.isArray(produtosData) ? produtosData : []);
        }
      })
      .catch(() => setErro("Erro ao carregar o vídeo."))
      .finally(() => setLoading(false));
  }, [videoId]);

  // Dimensões do vídeo
  const formato = video?.formato || "story";
  const { width: compositionWidth, height: compositionHeight } = getVideoDimensions(formato);

  // Calcular tamanho do player responsivo
  useEffect(() => {
    if (!video) return;

    const calcSize = () => {
      if (!containerRef.current) return;
      const { width: cw, height: ch } = containerRef.current.getBoundingClientRect();
      if (!cw || !ch) return;

      const scale = Math.min(cw / compositionWidth, ch / compositionHeight);
      setPlayerSize({
        width: Math.floor(compositionWidth * scale),
        height: Math.floor(compositionHeight * scale),
      });
    };

    calcSize();
    const timeout = setTimeout(calcSize, 100);
    const observer = new ResizeObserver(calcSize);
    if (containerRef.current) observer.observe(containerRef.current);

    return () => {
      clearTimeout(timeout);
      observer.disconnect();
    };
  }, [video, compositionWidth, compositionHeight]);

  // Duração do vídeo
  const fps = 30;
  const tempoLogo = 4 * fps;
  const duracaoTotal = (() => {
    let total = tempoLogo;
    if (produtos?.length) {
      produtos.forEach(p => {
        total += (Number(p?.tempo_audio) || 4) * fps;
      });
    }
    if (video?.outro_audio && Number(video?.tempo_outro) > 0) {
      total += (Number(video.tempo_outro) + 3) * fps;
    }
    return total;
  })();

  // === ESTADOS DE LOADING / ERRO ===

  if (loading) {
    return (
      <div style={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        background: "#1a1a2e",
        color: "#fff"
      }}>
        <div className="text-center">
          <div className="spinner-border text-light mb-3" role="status" />
          <p>Carregando vídeo...</p>
        </div>
      </div>
    );
  }

  if (erro) {
    return (
      <div style={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        background: "#1a1a2e",
        color: "#fff"
      }}>
        <div className="text-center">
          <i className="fa-solid fa-triangle-exclamation fa-3x mb-3" style={{ color: "#ffc107" }} />
          <h4>{erro}</h4>
          <p className="text-muted">Verifique o link e tente novamente.</p>
        </div>
      </div>
    );
  }

  // === PLAYER PÚBLICO ===

  return (
    <div style={{
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
      background: "#1a1a2e",
    }}>
      {/* Header simples */}
      <div style={{
        padding: "12px 20px",
        borderBottom: "1px solid rgba(255,255,255,0.1)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}>
        <span style={{ color: "#fff", fontWeight: "bold", fontSize: "1.1rem" }}>
          <i className="fa-solid fa-play-circle me-2" />
          VideoOferta
        </span>
      </div>

      {/* Player container */}
      <div
        ref={containerRef}
        style={{
          flex: 1,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: "20px",
          overflow: "hidden",
        }}
      >
        {playerSize.width > 0 && playerSize.height > 0 ? (
          <Player
            component={ProductVideo}
            durationInFrames={duracaoTotal}
            fps={fps}
            compositionWidth={compositionWidth}
            compositionHeight={compositionHeight}
            controls
            autoPlay
            style={{
              width: playerSize.width,
              height: playerSize.height,
              background: "#000",
              borderRadius: "10px",
            }}
            inputProps={(() => {
              const msgAberturaEnabled = !!(video?.msg_abertura_enabled === 1 || video?.msg_abertura_enabled === '1' || video?.msg_abertura_enabled === true);
              const msgEncerramentoEnabled = !!(video?.msg_encerramento_enabled === 1 || video?.msg_encerramento_enabled === '1' || video?.msg_encerramento_enabled === true);
              const redesEnabled = !!(video?.redes_enabled === 1 || video?.redes_enabled === '1' || video?.redes_enabled === true);
              const redesOutroEnabled = !!(video?.redes_outro === 1 || video?.redes_outro === '1' || video?.redes_outro === true);
              let cfg = {};
              try { cfg = typeof video?.redes_sociais === 'string' ? JSON.parse(video.redes_sociais || '{}') : (video?.redes_sociais || {}); } catch { cfg = {}; }
              const redesSociais = {
                instagram: video?.usuario_instagram || '',
                whatsapp:  video?.usuario_whatsapp  || '',
                facebook:  video?.usuario_facebook  || '',
                site:      video?.usuario_site      || '',
                endereco:  video?.usuario_endereco  || '',
                tiktok:    cfg.tiktok               || '',
                _config:   cfg._config || {},
              };
              return {
                selo: video.selo,
                background: video.background,
                products: produtos,
                intro: video.intro_audio,
                intro_time: video.tempo_audio,
                audio_fundo: video.url,
                backgroundVideo: "",
                cor_etiqueta: video.cor_etiqueta,
                bg_etiqueta: video.bg_etiqueta,
                fonte: '"Montserrat", sans-serif',
                logo: video.logo,
                formato: formato,
                msgAberturaEnabled,
                msgAberturaTexto: video?.msg_abertura_texto || '',
                msgEncerramentoEnabled,
                msgEncerramentoTexto: video?.msg_encerramento_texto || '',
                redesEnabled,
                redesOutroEnabled,
                redesSociais,
                msgDuracaoSegs: 3,
                outroAudio: video?.outro_audio || null,
                outroTime: video?.outro_audio && Number(video?.tempo_outro) > 0 ? Number(video.tempo_outro) + 3 : 0,
              };
            })()}
          />
        ) : (
          <div className="text-center text-white">
            <div className="spinner-border text-light mb-3" role="status" />
            <p>Carregando player...</p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{
        padding: "10px 20px",
        borderTop: "1px solid rgba(255,255,255,0.1)",
        textAlign: "center",
        color: "rgba(255,255,255,0.4)",
        fontSize: "0.8rem",
      }}>
        Criado com VideoOferta.com
      </div>
    </div>
  );
}

export default VideoPublico;
